import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMutation } from 'react-fetching-library';
import { useParams } from 'react-router-dom';
import MenuItem from '../../../../Shared/Table/widgets/MenuItem';
import { ReactComponent as EditIcon } from '../../../../../assets/icons/edit.svg';
import { ReactComponent as ResendIcon } from '../../../../../assets/icons/resend.svg';
import { ReactComponent as DownloadIcon } from '../../../../../assets/icons/file_download_black.svg';
import { ReactComponent as RemoveIcon } from '../../../../../assets/icons/remove.svg';
import showServerError from '../../../../../helpers/showError';
import { checkPermission } from '../../../../../helpers/checkPermissions';
import permissions from '../../../../../config/permissions';
import {
  enqueueSnackbar,
  SNACKBAR_VARIANTS,
  getTransitionWithVariable,
  updateCanShowShimmer,
} from '../../../../../redux_store';
import useMetadataUpdate from '../../../../../hooks/useMetadataUpdate';
import { orderStatusesActions } from '../../../constants';

const RowAction = ({
                     setAnchorEl,
                     row,
                     options,
                     setOpenRemoveDialog,
                     setRemovingOrder,
                     redirectToEditOrder,
                     setOrder,
                     reloadData,
                   }) => {
  const dispatch = useDispatch();
  const { en: { titles, notifications, errors } } = useSelector((state) => state.i18n);
  const { user } = useSelector((state) => state.user);
  const { accountUuid: paramAccountUuid } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [isStatusChangingLoading, setIsStatusChangingLoading] = useState(false);
  const {
    metadata: {
      currentAccount,
      currentAccountPermissions,
      childAccountPermissions,
    },
  } = useSelector((state) => state.user);

  const handleEditItemClick = (e, order) => {
    redirectToEditOrder(order);
  };

  const handleReorderItemClick = (e, order) => {
    const hasPermission = checkPermission(
      order.accountUUID === currentAccount.uuid
        ? currentAccountPermissions
        : childAccountPermissions,
      permissions.ORDER_CREATE,
    );
    if (hasPermission) {
      setOrder(order);
    } else {
      dispatch(
        enqueueSnackbar({
          message: notifications.notPermissionForCreatingOrder,
          options: {
            variant: SNACKBAR_VARIANTS.warning,
          },
        }),
      );
    }
  };

  const handleRemoveItemClick = (e, order) => {
    const hasPermission = checkPermission(
      paramAccountUuid
        ? currentAccountPermissions
        : childAccountPermissions,
      permissions.ORDER_DELETE,
    );
    if (hasPermission) {
      setOpenRemoveDialog(true);
      setRemovingOrder(order);
    } else {
      dispatch(
        enqueueSnackbar({
          message: notifications.notPermissionForDeletingOrder,
          options: {
            variant: SNACKBAR_VARIANTS.warning,
          },
        }),
      );
    }
  };

  const getImmunotherapyReportPDF = (orderUUID, accountUUID) => ({
    method: 'GET',
    endpoint: `/accounts/${accountUUID}/orders/${orderUUID}/immunotherapy-report`,
    responseType: 'blob',
    headers: {
      'Content-Type': 'application/pdf',
    },
  });

  const { mutate: getPDFFile } = useMutation(getImmunotherapyReportPDF);

  const downloadedPDFFile = async (e, order) => {
    setIsLoading(true);
    const { payload, error, status } = await getPDFFile(order.uuid, order.accountUUID);
    if (error) {
      const options = payload && {
        correlationUUID: payload.correlationUUID,
        userUUID: user.uuid,
      };
      switch (status) {
        case 422: {
          if (payload.validationErrors) {
            Object.keys(payload.validationErrors).forEach((key) =>
              showServerError(dispatch, payload.validationErrors[key]),
            );
            break;
          }
          showServerError(dispatch, errors.validationError);
          break;
        }
        case 401: {
          showServerError(dispatch, notifications.notPermissionForDownloadingFile);
          break;
        }
        default: {
          showServerError(dispatch, '', options);
          break;
        }
      }
    } else if (payload && payload.size > 0) {
      const url = window.URL.createObjectURL(new Blob([payload], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Immunotherapy report - ${order.patientLastName} ${order.patientFirstName} ${order.orderDate}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    }
    setIsLoading(false);
    setAnchorEl(null);
  };

  const actions = useMemo(
    () => [
      {
        key: '1',
        icon: <EditIcon />,
        onActionClick: handleEditItemClick,
        children: titles.editOrder,
        addDivider: true,
      },
      {
        key: '2',
        icon: <ResendIcon />,
        onActionClick: handleReorderItemClick,
        children: titles.reorder,
      },
      {
        key: 'downloadImmunotherapyReport',
        icon: <DownloadIcon />,
        onActionClick: downloadedPDFFile,
        children: titles.downloadImmunotherapyReport,
        doNotAutoClose: true,
      },
      {
        key: '3',
        icon: <RemoveIcon />,
        onActionClick: handleRemoveItemClick,
        children: titles.removeOrder,
        isRemove: true,
      },
    ],
    [isLoading],
  );

  const statusChangeAction = (accountUuid, values) => {
    return {
      method: 'POST',
      endpoint: `/accounts/${accountUuid}/orders/actions`,
      body: values,
    };
  };

  const { mutate } = useMetadataUpdate(useMutation, [statusChangeAction]);

  const handleStatusChange = async (order, newStatus) => {
    setIsStatusChangingLoading(newStatus);
    const statusItem = orderStatusesActions.find(
      (orderStatus) => orderStatus.value === newStatus,
    );
    const { status, error, payload } = await mutate(order.accountUUID, {
        statusAction: statusItem.value,
        orderUUIDs: [order.uuid],
      },
    );
    if (error) {
      switch (status) {
        case 422:
        case 500: {
          if (payload.validationErrors) {
            Object.keys(payload.validationErrors).forEach((key) =>
              showServerError(dispatch, payload.validationErrors[key]),
            );
            break;
          }
          showServerError(dispatch, errors.validationError);
          break;
        }
        default: {
          showServerError(dispatch, errors.unknownError);
          break;
        }
      }
    } else {
      dispatch(updateCanShowShimmer(true));
      reloadData(true, 300);
      dispatch(
        enqueueSnackbar({
          message: getTransitionWithVariable(notifications.orderStatusChanged, {
            name: titles.order,
          }),
          options: {
            variant: SNACKBAR_VARIANTS.success,
          },
        }),
      );
    }
    setIsStatusChangingLoading(null);
    setAnchorEl(null);
  };


  return <>
    {
      options.map((props) => {
        const { children, icon, value } = props;
        return <MenuItem
          title={children}
          icon={icon}
          isLoading={value === isStatusChangingLoading}
          setAnchorEl={() => {
          }}
          onClick={() => handleStatusChange(row.original, value)}
        />;
      })
    }
    {
      actions.map((props) => {
        const { key, children, onActionClick, icon, addDivider, isRemove, doNotAutoClose } = props;
        return <MenuItem
          title={children}
          icon={icon}
          isLoading={key === 'downloadImmunotherapyReport' && isLoading}
          onClick={(e) => onActionClick(e, row.original)}
          setAnchorEl={doNotAutoClose ? () => {
          } : setAnchorEl}
          hasTopDriver={addDivider}
          isErrorStyle={isRemove}
        />;
      })
    }
  </>;
};

export default RowAction;
